import React from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody } from "reactstrap";
import neoLogo from "../../assets/images/Neo_Logo_A.svg";

/**
 * NeoPrintModal
 * Componente de Impressão Global e de Alta Fidelidade do Neo Design System.
 * Compartilhável entre todas as listagens do sistema (CRM, Clientes, Financeiro, DRE, etc.).
 * 
 * Características:
 *  - 100% livre de CSS inline e tags de estilo dinâmico (<style>) no JSX.
 *  - Todas as regras de mídia e layouts de página (@media print e @page) delegadas ao SCSS global.
 *  - Suporta orientações retrato ("portrait") e paisagem ("landscape") de forma limpa via classes e named pages.
 *  - Executa as lógicas de renderização customizada das colunas (render) declaradas no data table original.
 */
const NeoPrintModal = ({
    isOpen,
    toggle,
    title = "Relatório",
    subtitle = "",
    data = [],
    columns = [],
    tenantName = "",
    tenantLogo = "",
    orientation = "portrait" // "portrait" | "landscape"
}) => {
    const handlePrint = () => {
        window.print();
    };

    const dateStr = new Date().toLocaleDateString("pt-BR");
    const timeStr = new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            size="xl"
            centered
            contentClassName="border-0 shadow-lg position-relative overflow-hidden"
            backdropClassName="bg-dark bg-opacity-75"
            dialogClassName={orientation === "landscape" ? "neo-print-modal-dialog-landscape" : "neo-print-modal-dialog"}
        >
            {/* Cabeçalho Interativo do Modal (apenas tela) */}
            <div className="no-print neo-print-modal-header-bar">
                <div>
                    <h5 className="neo-print-modal-header-title">
                        <i className="mdi mdi-printer text-accent me-2" />
                        Visualização de Impressão — {title}
                    </h5>
                    <p className="neo-print-modal-header-subtitle">
                        Confira o relatório formatado em modo {orientation === "landscape" ? "Paisagem" : "Retrato"}. As colunas correspondem à sua listagem ativa.
                    </p>
                </div>
                <div className="d-flex gap-2">
                    <button
                        onClick={handlePrint}
                        className="btn btn-primary d-flex align-items-center gap-2 px-3 fw-bold shadow-sm neo-print-modal-btn"
                    >
                        <i className="mdi mdi-printer" /> Imprimir Relatório
                    </button>
                    <button
                        onClick={toggle}
                        className="btn btn-light px-3 fw-semibold border neo-print-modal-btn"
                    >
                        Fechar
                    </button>
                </div>
            </div>

            {/* Visualização Visual do Relatório */}
            <ModalBody className="neo-print-modal-body">
                <div 
                    id="printable-neo-report-container" 
                    className={`neo-print-sheet ${orientation === "landscape" ? "neo-print-sheet-landscape" : "neo-print-sheet-portrait"}`}
                >
                    
                    {/* Header do Relatório */}
                    <div className="neo-print-report-header">
                        <div>
                            {tenantLogo ? (
                                <img src={tenantLogo} alt="Logo" className="neo-print-report-logo-img" />
                            ) : (
                                <h4 className="fw-bold m-0 text-dark">{tenantName || "NeoApp"}</h4>
                            )}
                        </div>
                        <div className="text-end">
                            <h6 className="m-0 fw-bold text-muted font-size-12 text-uppercase tracking-wider">
                                {tenantName || "CENTRO ESPORTIVO"}
                            </h6>
                            <h4 className="neo-print-report-header-title mt-1">
                                {title}
                            </h4>
                            {subtitle && (
                                <span className="neo-print-report-header-subtitle">
                                    {subtitle}
                                </span>
                            )}
                            <div className="neo-print-report-header-meta">
                                <strong>Registros:</strong> {data.length} | <strong>Data:</strong> {dateStr} às {timeStr}
                            </div>
                        </div>
                    </div>

                    {/* Tabela do Relatório */}
                    <div className="table-responsive">
                        <table className="neo-print-table">
                            <thead>
                                <tr>
                                    {columns.map((col, idx) => (
                                        <th 
                                            key={idx} 
                                            className={col.align === "center" ? "text-center" : (col.align === "right" ? "text-end" : "text-start")}
                                        >
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((row, i) => (
                                    <tr key={row.id || i}>
                                        {columns.map((col, idx) => {
                                            const align = col.align === "center" ? "text-center" : (col.align === "right" ? "text-end" : "text-start");
                                            
                                            // Executa lógicas de render do array de colunas ou lê valor simples
                                            let cellContent = "";
                                            if (col.key === "index") {
                                                cellContent = i + 1;
                                            } else if (col.render) {
                                                // Mapeia paginação mockada para manter a compatibilidade do render
                                                cellContent = col.render(row, i, { page: 1, rowsPerPage: 999999 });
                                            } else {
                                                cellContent = row[col.key] !== undefined && row[col.key] !== null ? String(row[col.key]) : "—";
                                            }

                                            return (
                                                <td key={idx} className={align}>
                                                    {cellContent}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Rodapé Branded */}
                    <div className="neo-print-report-footer">
                        <div className="text-end">
                            <p className="neo-print-report-footer-time">
                                Relatório oficial gerado via NeoApp.
                            </p>
                            <div className="neo-print-report-footer-powered">
                                <span className="neo-print-report-footer-powered-span">POWERED BY</span>
                                <img src={neoLogo} alt="NeoApp" className="neo-print-report-footer-logo" />
                            </div>
                        </div>
                    </div>

                </div>
            </ModalBody>
        </Modal>
    );
};

NeoPrintModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    data: PropTypes.array,
    columns: PropTypes.array.isRequired,
    tenantName: PropTypes.string,
    tenantLogo: PropTypes.string,
    orientation: PropTypes.oneOf(["portrait", "landscape"])
};

export default NeoPrintModal;
